const LOCAL_ORIGIN = 'http://localhost'
const NEXT_IMAGE_PATH = '/_next/image'
const COS_SIGN_PATH = '/api/storage/sign'
const STABLE_MEDIA_PATH_PREFIX = '/m/'
const MAX_NEXT_UNWRAP_DEPTH = 5
const STORAGE_KEY_PREFIXES = ['images/', 'video/', 'voice/'] as const
const PREVIEWABLE_IMAGE_EXTENSIONS = /\.(?:png|jpe?g|webp|gif|bmp|tiff?|heic)$/i
const DEFAULT_PREVIEW_WIDTH = 640
const LARGE_PREVIEW_WIDTH = 1600
const DEFAULT_PREVIEW_QUALITY = 75
const PREVIEW_ROUTE_VERSION = '2'
const PREVIEW_URL_CACHE_LIMIT = 512
const previewImageUrlCache = new Map<string, string | null>()

export type ImagePreviewProcess = {
  process: string
  width: number
  quality: number | null
}

function isStorageKey(value: string): boolean {
  return STORAGE_KEY_PREFIXES.some((prefix) => value.startsWith(prefix))
}

function isAbsoluteUrl(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(value)
}

function tryParseUrl(value: string): URL | null {
  try {
    return new URL(value, LOCAL_ORIGIN)
  } catch {
    return null
  }
}

function stringifyUrl(parsed: URL, original: string): string {
  if (isAbsoluteUrl(original)) return parsed.toString()
  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

function hasPreviewableImageExtension(value: string): boolean {
  const parsed = tryParseUrl(value)
  const path = parsed ? parsed.pathname : value.split(/[?#]/, 1)[0] || value
  return PREVIEWABLE_IMAGE_EXTENSIONS.test(path)
}

function isOpaqueInlineImage(value: string): boolean {
  return value.startsWith('data:') || value.startsWith('blob:')
}

function buildImagePreviewProcess(width = DEFAULT_PREVIEW_WIDTH, quality: number | null = null): string {
  const safeWidth = Number.isFinite(width) && width > 0 ? Math.min(Math.max(Math.round(width), 16), 2048) : DEFAULT_PREVIEW_WIDTH
  const safeQuality = typeof quality === 'number' && Number.isFinite(quality)
    ? Math.min(Math.max(Math.round(quality), 1), 100)
    : null
  return safeQuality
    ? `image/resize,w_${safeWidth}/format,webp/quality,q_${safeQuality}`
    : `image/resize,w_${safeWidth}/format,webp`
}

export function normalizeImagePreviewProcess(input: string | null | undefined): ImagePreviewProcess | null {
  if (!input) return null
  const trimmed = input.trim()
  const match = trimmed.match(/^image\/resize,w_(\d{1,5})\/format,webp(?:\/quality,q_(\d{1,3}))?$/)
  if (!match) return null
  const width = Number.parseInt(match[1] || '', 10)
  const quality = match[2] ? Number.parseInt(match[2], 10) : null
  if (!Number.isFinite(width) || width < 16 || width > 2048) return null
  if (quality !== null && (!Number.isFinite(quality) || quality < 1 || quality > 100)) return null
  return {
    process: buildImagePreviewProcess(width, quality),
    width,
    quality,
  }
}

export function getDefaultImagePreviewProcess(): ImagePreviewProcess {
  return {
    process: buildImagePreviewProcess(DEFAULT_PREVIEW_WIDTH),
    width: DEFAULT_PREVIEW_WIDTH,
    quality: DEFAULT_PREVIEW_QUALITY,
  }
}

export function unwrapNextImageUrl(input: string): string {
  let current = input.trim()
  if (!current) return current

  for (let i = 0; i < MAX_NEXT_UNWRAP_DEPTH; i += 1) {
    const parsed = tryParseUrl(current)
    if (!parsed || parsed.pathname !== NEXT_IMAGE_PATH) {
      return current
    }

    const nestedUrl = parsed.searchParams.get('url')
    if (!nestedUrl) {
      return current
    }

    let decoded = nestedUrl
    try {
      decoded = decodeURIComponent(nestedUrl)
    } catch {
      decoded = nestedUrl
    }

    if (!decoded || decoded === current) {
      return current
    }

    current = decoded
  }

  return current
}

function toDisplayImageUrlBase(input: string | null | undefined): string | null {
  if (!input) return null
  const raw = input.trim()
  if (!raw) return null

  const unwrapped = unwrapNextImageUrl(raw)
  if (isStorageKey(unwrapped)) {
    return `${COS_SIGN_PATH}?key=${encodeURIComponent(unwrapped)}`
  }

  return unwrapped
}

export function withOssImageProcess(input: string, process: string, options: { replaceExisting?: boolean } = {}): string {
  if (!input || !process || isOpaqueInlineImage(input)) return input
  const parsed = tryParseUrl(input)
  if (!parsed) return input

  if (parsed.pathname === COS_SIGN_PATH || parsed.pathname.startsWith(STABLE_MEDIA_PATH_PREFIX)) {
    if (options.replaceExisting || !parsed.searchParams.has('process')) {
      parsed.searchParams.set('process', process)
    }
    if (!parsed.searchParams.has('pv')) {
      parsed.searchParams.set('pv', PREVIEW_ROUTE_VERSION)
    }
    return stringifyUrl(parsed, input)
  }

  if (hasPreviewableImageExtension(input) && (options.replaceExisting || !parsed.searchParams.has('x-oss-process'))) {
    parsed.searchParams.set('x-oss-process', process)
  }

  return stringifyUrl(parsed, input)
}

function toProcessedPreviewImageUrl(
  input: string | null | undefined,
  width = DEFAULT_PREVIEW_WIDTH,
  options: { replaceExistingProcess?: boolean } = {},
): string | null {
  const cacheKey = typeof input === 'string' ? `${Math.round(width)}:${input}` : null
  if (cacheKey && previewImageUrlCache.has(cacheKey)) {
    const cached = previewImageUrlCache.get(cacheKey) ?? null
    previewImageUrlCache.delete(cacheKey)
    previewImageUrlCache.set(cacheKey, cached)
    return cached
  }
  const base = toDisplayImageUrlBase(input)
  if (!base || isOpaqueInlineImage(base)) {
    if (cacheKey) rememberPreviewImageUrl(cacheKey, base)
    return base
  }

  const process = buildImagePreviewProcess(width)
  const parsed = tryParseUrl(base)
  let result: string | null
  if (parsed?.pathname === COS_SIGN_PATH) {
    const key = parsed.searchParams.get('key') || ''
    result = hasPreviewableImageExtension(key) ? withOssImageProcess(base, process, { replaceExisting: options.replaceExistingProcess }) : base
  } else if (parsed?.pathname.startsWith(STABLE_MEDIA_PATH_PREFIX)) {
    result = withOssImageProcess(base, process, { replaceExisting: options.replaceExistingProcess })
  } else {
    result = hasPreviewableImageExtension(base) ? withOssImageProcess(base, process, { replaceExisting: options.replaceExistingProcess }) : base
  }

  if (cacheKey) rememberPreviewImageUrl(cacheKey, result)
  return result
}

export function toPreviewImageUrl(input: string | null | undefined): string | null {
  return toProcessedPreviewImageUrl(input, DEFAULT_PREVIEW_WIDTH)
}

export function toLargePreviewImageUrl(input: string | null | undefined): string | null {
  return toProcessedPreviewImageUrl(input, LARGE_PREVIEW_WIDTH, { replaceExistingProcess: true })
}

function rememberPreviewImageUrl(input: string, output: string | null) {
  previewImageUrlCache.set(input, output)
  if (previewImageUrlCache.size <= PREVIEW_URL_CACHE_LIMIT) return
  const oldestKey = previewImageUrlCache.keys().next().value
  if (typeof oldestKey === 'string') previewImageUrlCache.delete(oldestKey)
}

export function toDisplayImageUrl(input: string | null | undefined): string | null {
  return toPreviewImageUrl(input)
}

function toLitePptTextBearingStorageKey(key: string): string | null {
  const normalized = key.trim()
  if (!normalized || normalized.includes('.text-bearing.')) return null
  const next = normalized.replace(/(^|\/)(page-\d+)\.webp$/i, '$1$2.text-bearing.webp')
  return next === normalized ? null : next
}

export function toLitePptTextBearingPreviewUrl(input: string | null | undefined): string | null {
  if (!input) return null
  const raw = input.trim()
  if (!raw) return null
  const unwrapped = unwrapNextImageUrl(raw)

  if (isStorageKey(unwrapped)) {
    const key = toLitePptTextBearingStorageKey(unwrapped)
    return key ? toPreviewImageUrl(key) : null
  }

  const parsed = tryParseUrl(unwrapped)
  if (parsed?.pathname === COS_SIGN_PATH) {
    const key = parsed.searchParams.get('key') || ''
    const nextKey = toLitePptTextBearingStorageKey(key)
    if (!nextKey) return null
    parsed.searchParams.set('key', nextKey)
    return withOssImageProcess(stringifyUrl(parsed, unwrapped), buildImagePreviewProcess(DEFAULT_PREVIEW_WIDTH))
  }

  if (parsed) {
    const nextPath = parsed.pathname.replace(/(^|\/)(page-\d+)\.webp$/i, '$1$2.text-bearing.webp')
    if (nextPath === parsed.pathname) return null
    parsed.pathname = nextPath
    return withOssImageProcess(stringifyUrl(parsed, unwrapped), buildImagePreviewProcess(DEFAULT_PREVIEW_WIDTH))
  }

  return null
}

export function resolveOriginalImageUrl(input: string | null | undefined): string | null {
  if (!input) return null
  const raw = input.trim()
  if (!raw) return null

  const unwrapped = unwrapNextImageUrl(raw)
  if (isStorageKey(unwrapped)) {
    return `${COS_SIGN_PATH}?key=${encodeURIComponent(unwrapped)}`
  }

  const parsed = tryParseUrl(unwrapped)
  if (parsed?.pathname === COS_SIGN_PATH) {
    const key = parsed.searchParams.get('key')
    if (key) {
      let decodedKey = key
      try {
        decodedKey = decodeURIComponent(key)
      } catch {
        decodedKey = key
      }
      return `${COS_SIGN_PATH}?key=${encodeURIComponent(decodedKey)}`
    }
  }

  if (parsed?.pathname.startsWith(STABLE_MEDIA_PATH_PREFIX)) {
    parsed.searchParams.delete('process')
    parsed.searchParams.delete('pv')
    return stringifyUrl(parsed, unwrapped)
  }

  if (parsed?.searchParams.has('x-oss-process')) {
    parsed.searchParams.delete('x-oss-process')
    return stringifyUrl(parsed, unwrapped)
  }

  return unwrapped
}
